import { PocRadio } from 'commons/components/components-wrapper/poc-radio.component';
import { PocRadioGroup } from 'commons/components/components-wrapper/poc-radio-group.component';
import { useTranslation } from 'core/i18n/i18n';
import { EapCypherType, EapSettings } from 'modules/settings/pods/settings-devices/settings-devices-menu.model';
import { useEapSettingsErrorValidationMessageForm } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/hooks/use-eap-settings-error-validation-message-form/use-eap-settings-error-validation-message-form.hook';
import { useEapSettingsFormValidation } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/hooks/use-eap-settings-validation-form/use-eap-settings-validation-form.hook';
import {
    EapFormCard,
    EapFormTitle,
    EapOwcInput,
} from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/settings-devices-eap-settings-form.style';
import { EAP_NAME_MAX_LENGTH, EAP_SSID_MAX_LENGTH } from './eap-settings-form-general.constants';
import {
    EAP_CYPHER_TYPE_OPTIONS,
    EapSettingsFormGeneralModel,
    newEapFormValidation,
} from './eap-settings-form-general.model';

interface EapSettingsFormGeneralProps {
    data: EapSettingsFormGeneralModel;
    mode?: 'edit' | 'create';
    onDataChange?: <K extends keyof EapSettings>(propertyName: K, value: EapSettings[K]) => void;
}

export const EapSettingsFormGeneral = ({ data, mode, onDataChange }: EapSettingsFormGeneralProps) => {
    const { eapSettingsFormValidatorError, handleError } = useEapSettingsFormValidation(newEapFormValidation);
    const { errorMessages } = useEapSettingsErrorValidationMessageForm();
    const { t } = useTranslation();
    return (
        <EapFormCard>
            <div>
                <EapFormTitle>
                    {t('settings.devices.eap-settings.eap-config-create-form.form.field.eap-name')}
                </EapFormTitle>
                <EapOwcInput
                    name="name"
                    onChange={(_, value) => onDataChange?.('name', value as string)}
                    value={data.name}
                    protection={mode === 'edit' ? 'readonly' : 'none'}
                    placeholder={t(
                        'settings.devices.eap-settings.eap-config-create-form.form.field.eap-name.placeholder'
                    )}
                    required
                    autofocus
                    spaceForError
                    maxlength={EAP_NAME_MAX_LENGTH}
                    maxValue={EAP_NAME_MAX_LENGTH}
                    validity={eapSettingsFormValidatorError.name}
                    onValidity={(_, val) => handleError('name', val)}
                    validationMessages={errorMessages.name}
                />
            </div>
            <div>
                <EapFormTitle>{t('settings.devices.eap-settings.eap-config-create-form.form.field.ssid')}</EapFormTitle>
                <EapOwcInput
                    name="ssid"
                    onChange={(_, value) => onDataChange?.('ssid', value as string)}
                    value={data.ssid}
                    placeholder={t('settings.devices.eap-settings.eap-config-create-form.form.field.ssid.placeholder')}
                    required
                    maxlength={EAP_SSID_MAX_LENGTH}
                    maxValue={EAP_SSID_MAX_LENGTH}
                    spaceForError
                    validity={eapSettingsFormValidatorError.ssid}
                    onValidity={(_, val) => handleError('ssid', val)}
                    validationMessages={errorMessages.ssid}
                />
            </div>
            <div>
                <EapFormTitle>
                    {t('settings.devices.eap-settings.eap-config-create-form.form.field.crypto-algorithm')}
                </EapFormTitle>
                <PocRadioGroup
                    name="cipherType"
                    direction={'vertical'}
                    value={data.cipherType}
                    onValueChange={(value) => {
                        onDataChange?.('cipherType', value.detail as EapCypherType);
                    }}
                >
                    {EAP_CYPHER_TYPE_OPTIONS.map((option) => (
                        <PocRadio key={option.value} value={option.value}>
                            {t(
                                `settings.devices.eap-settings.eap-config-create-form.form.field.crypto-algorithm.${option.label}`
                            )}
                        </PocRadio>
                    ))}
                </PocRadioGroup>
            </div>
        </EapFormCard>
    );
};
